/**
 * Legacy rule, kept as the fallback when a tab has no configured share layout:
 * sheet columns that are really a payment line rather than a spec (the LAPTOP
 * sheet's "UPI" column) get a blank line before them in the shared message so
 * the specs read as one block and the money line stands apart.
 */
export const PAYMENT_ROW_FALLBACK = /^\s*UPI\b/i;
/** The display label for a sheet header: the admin's override, else the header. */
export function displayLabel(name, fmt) {
    const override = fmt?.labels?.[name];
    const trimmed = override?.trim();
    return trimmed ? trimmed : name;
}
/** Spec-table rows with label overrides and section breaks applied. */
export function specTableRows(rows, fmt) {
    const breaks = new Set(fmt?.tableBreaksBefore ?? []);
    return rows.map(([key, value], i) => ({
        key,
        label: displayLabel(key, fmt),
        value,
        breakBefore: i > 0 && breaks.has(key),
    }));
}
/**
 * The spec block of the share/copy message as lines ('' = blank separator).
 * With a configured layout: its order, hidden fields left out, blank items
 * kept, and headers missing from the layout (columns added to the sheet after
 * the config was saved) appended at the end. Without one: the rows in their
 * given order with the legacy UPI blank-line rule. Edge/duplicate blanks are
 * tidied either way.
 */
export function buildSpecShareLines(rows, fmt) {
    const layout = fmt?.share;
    const lines = [];
    if (!layout || layout.length === 0) {
        let separated = false;
        for (const [key, value] of rows) {
            if (!separated && PAYMENT_ROW_FALLBACK.test(key)) {
                lines.push('');
                separated = true;
            }
            lines.push(`${displayLabel(key, fmt)}: ${value}`);
        }
        return tidyBlankLines(lines);
    }
    const byKey = new Map(rows);
    const claimed = new Set();
    for (const item of layout) {
        if (item.kind === 'blank') {
            lines.push('');
            continue;
        }
        claimed.add(item.name);
        if (!item.show)
            continue;
        const value = byKey.get(item.name);
        if (value !== undefined && value !== '') {
            lines.push(`${displayLabel(item.name, fmt)}: ${value}`);
        }
    }
    for (const [key, value] of rows) {
        if (!claimed.has(key))
            lines.push(`${displayLabel(key, fmt)}: ${value}`);
    }
    return tidyBlankLines(lines);
}
/** Drop leading/trailing blank lines and collapse runs of blanks to one. */
function tidyBlankLines(lines) {
    const out = [];
    for (const line of lines) {
        if (line === '' && (out.length === 0 || out[out.length - 1] === ''))
            continue;
        out.push(line);
    }
    while (out.length > 0 && out[out.length - 1] === '')
        out.pop();
    return out;
}
//# sourceMappingURL=display.js.map